// Make sure you’ve installed Formspree:
// npm install @formspree/react

import React, { useState } from 'react';
import { useForm, ValidationError } from '@formspree/react';
import styles from './ContactPage.module.css';

const ContactPage = () => {
  const [step, setStep] = useState(1);
  const totalSteps = 5;
  const [formData, setFormData] = useState({
    // Stage 1
    fullName: '',
    email: '',
    phone: '',
    businessName: '',
    businessWebsite: '',
    // Stage 2
    services: [],
    otherService: '',
    // Stage 3
    websiteType: '',
    otherWebsiteType: '',
    domainHosting: 'No',
    pageCount: 5,
    contentReady: 'Yes',
    contentExplanation: '',
    designPreference: '',
    exampleWebsites: '',
    specialFeatures: [],
    otherFeature: '',
    designType: [],
    otherDesignType: '',
    designStyle: '',
    colorPreferences: '',
    designInspirations: '',
    hasLogo: 'No',
    vaTasks: [],
    otherVaTask: '',
    hoursPerWeek: '',
    preferredTimezone: '',
    // Stage 4
    startDate: '',
    deadline: '',
    budget: '',
    additionalNotes: '',
    // Stage 5 (Confirmation)
    howDidYouHear: '',
    consent: false,
  });

  const nextStep = () => setStep(prev => prev + 1);
  const prevStep = () => setStep(prev => prev - 1);

  const handleChange = (input) => (e) => {
    const { type, value, checked } = e.target;

    if (type === 'checkbox') {
      if (['services', 'specialFeatures', 'designType', 'vaTasks'].includes(input)) {
        const current = formData[input] || [];
        setFormData({
          ...formData,
          [input]: checked ? [...current, value] : current.filter(i => i !== value),
        });
      } else {
        setFormData({ ...formData, [input]: checked });
      }
    } else {
      setFormData({ ...formData, [input]: value });
    }
  };

  const progress = (step / totalSteps) * 100;

  const renderStep = () => {
    switch (step) {
      case 1:
        return <Stage1 nextStep={nextStep} handleChange={handleChange} values={formData} />;
      case 2:
        return <Stage2 nextStep={nextStep} prevStep={prevStep} handleChange={handleChange} values={formData} />;
      case 3:
        return <Stage3 nextStep={nextStep} prevStep={prevStep} handleChange={handleChange} values={formData} />;
      case 4:
        return <Stage4 nextStep={nextStep} prevStep={prevStep} handleChange={handleChange} values={formData} />;
      case 5:
        return <Stage5 prevStep={prevStep} values={formData} />;
      default:
        return null;
    }
  };

  return (
    <div className={styles.contactPage}>
      <div className={styles.formContainer}>
        <div className={styles.progressBarContainer}>
          <div className={styles.progressBar} style={{ width: `${progress}%` }}></div>
        </div>
        <h2 className={styles.stepTitle}>Step {step} of {totalSteps}</h2>
        {renderStep()}
      </div>
    </div>
  );
};

// ---------- Stage 1 ----------
const Stage1 = ({ nextStep, handleChange, values }) => (
  <div>
    <h3 className={styles.stageHeading}>Basic Information</h3>
    <div className={styles.inputGroup}>
      <label>Full Name *</label>
      <input type="text" onChange={handleChange('fullName')} value={values.fullName} required />
    </div>
    <div className={styles.inputGroup}>
      <label>Email Address *</label>
      <input type="email" onChange={handleChange('email')} value={values.email} required />
    </div>
    <div className={styles.inputGroup}>
      <label>Phone Number</label>
      <input type="tel" onChange={handleChange('phone')} value={values.phone} />
    </div>
    <div className={styles.inputGroup}>
      <label>Business Name</label>
      <input type="text" onChange={handleChange('businessName')} value={values.businessName} />
    </div>
    <div className={styles.inputGroup}>
      <label>Business Website (if any)</label>
      <input type="url" onChange={handleChange('businessWebsite')} value={values.businessWebsite} placeholder="https://example.com" />
    </div>
    <button onClick={nextStep} className={styles.nextButton}>Next</button>
  </div>
);

// ---------- Stage 2 ----------
const Stage2 = ({ nextStep, prevStep, handleChange, values }) => (
  <div>
    <h3 className={styles.stageHeading}>Service Selection</h3>
    <div className={styles.checkboxGroup}>
      {['Website Development', 'Graphic Design', 'Virtual Assistant', 'Social Media Management', 'Digital Marketing', 'E-commerce Setup', 'Other'].map(service => (
        <label key={service} className={styles.checkboxLabel}>
          <input
            type="checkbox"
            name="services"
            value={service}
            checked={values.services.includes(service)}
            onChange={handleChange('services')}
          />
          {service}
        </label>
      ))}
    </div>
    {values.services.includes('Other') && (
      <div className={styles.inputGroup}>
        <label>Please specify other service:</label>
        <input type="text" onChange={handleChange('otherService')} value={values.otherService} />
      </div>
    )}
    <div className={styles.buttonGroup}>
      <button onClick={prevStep} className={styles.prevButton}>Back</button>
      <button onClick={nextStep} className={styles.nextButton}>Next</button>
    </div>
  </div>
);

// ---------- Stage 3 ----------
const Stage3 = ({ nextStep, prevStep, handleChange, values }) => (
  <div>
    <h3 className={styles.stageHeading}>Service Details</h3>

    {values.services.includes('Website Development') && (
      <div className={styles.serviceSection}>
        <h4>Website Development Details</h4>
        <div className={styles.inputGroup}>
          <label>Website Type</label>
          <select onChange={handleChange('websiteType')} value={values.websiteType}>
            <option value="">Select a type...</option>
            <option value="Business/Portfolio">Business/Portfolio</option>
            <option value="Blog/News">Blog/News</option>
            <option value="E-commerce">E-commerce</option>
            <option value="Booking/Appointment">Booking/Appointment</option>
            <option value="Membership/Community">Membership/Community</option>
            <option value="Other">Other</option>
          </select>
        </div>
        {values.websiteType === 'Other' && (
          <input type="text" placeholder="Please specify" onChange={handleChange('otherWebsiteType')} value={values.otherWebsiteType} />
        )}
      </div>
    )}

    {values.services.includes('Graphic Design') && (
      <div className={styles.serviceSection}>
        <h4>Graphic Design Details</h4>
        <label>Type of design needed</label>
        <div className={styles.checkboxGroup}>
          {['Logo', 'Full Branding', 'Business Card', 'Social Media Kit', 'Brochure/Flyer', 'Poster', 'Other'].map(type => (
            <label key={type} className={styles.checkboxLabel}>
              <input
                type="checkbox"
                name="designType"
                value={type}
                checked={values.designType.includes(type)}
                onChange={handleChange('designType')}
              />
              {type}
            </label>
          ))}
        </div>
        {values.designType.includes('Other') && (
          <input type="text" placeholder="Please specify" onChange={handleChange('otherDesignType')} value={values.otherDesignType} />
        )}
      </div>
    )}

    {values.services.includes('Virtual Assistant') && (
      <div className={styles.serviceSection}>
        <h4>Virtual Assistant Details</h4>
        <label>Type of tasks needed</label>
        <div className={styles.checkboxGroup}>
          {['Email Management', 'Scheduling/Appointments', 'Data Entry', 'Research', 'Customer Support', 'Social Media Posting', 'Other'].map(task => (
            <label key={task} className={styles.checkboxLabel}>
              <input
                type="checkbox"
                name="vaTasks"
                value={task}
                checked={values.vaTasks.includes(task)}
                onChange={handleChange('vaTasks')}
              />
              {task}
            </label>
          ))}
        </div>
        {values.vaTasks.includes('Other') && (
          <input type="text" placeholder="Please specify" onChange={handleChange('otherVaTask')} value={values.otherVaTask} />
        )}
      </div>
    )}

    <div className={styles.buttonGroup}>
      <button onClick={prevStep} className={styles.prevButton}>Back</button>
      <button onClick={nextStep} className={styles.nextButton}>Next</button>
    </div>
  </div>
);

const Stage4 = ({ nextStep, prevStep, handleChange, values }) => (
  <div>
    <h3 className={styles.stageHeading}>Project Timeline & Budget</h3>
    <div className={styles.inputGroup}>
      <label>Preferred Start Date</label>
      <input type="date" onChange={handleChange('startDate')} value={values.startDate} />
    </div>
    <div className={styles.inputGroup}>
      <label>Deadline (if any)</label>
      <input type="date" onChange={handleChange('deadline')} value={values.deadline} />
    </div>
    <div className={styles.inputGroup}>
      <label>Estimated Budget Range</label>
      <select onChange={handleChange('budget')} value={values.budget}>
        <option value="">Select a budget...</option>
        <option value="Under R2000">Under R2000</option>
        <option value="R2000–R5000">R2000–R5000</option>
        <option value="R5000–R10,000">R5000–R10,000</option>
        <option value="R10000+">R10000+</option>
      </select>
    </div>
    <div className={styles.inputGroup}>
      <label>Additional Notes/Requirements</label>
      <textarea rows="4" onChange={handleChange('additionalNotes')} value={values.additionalNotes}></textarea>
    </div>
    <div className={styles.buttonGroup}>
      <button onClick={prevStep} className={styles.prevButton}>Back</button>
      <button onClick={nextStep} className={styles.nextButton}>Next</button>
    </div>
  </div>
);

const Stage5 = ({ prevStep, values }) => {
  const [state, handleSubmit] = useForm("meevldvd");

  if (state.succeeded) {
    return <p>Thank you for your submission! We’ll contact you soon.</p>;
  }

  return (
    <form onSubmit={handleSubmit}>
      <h3 className={styles.stageHeading}>Confirmation</h3>

      {Object.entries(values).map(([key, value]) => (
        <input key={key} type="hidden" name={key} value={Array.isArray(value) ? value.join(', ') : value} />
      ))}

      <div className={styles.inputGroup}>
        <label>How did you hear about us?</label>
        <select name="howDidYouHear" defaultValue={values.howDidYouHear}>
          <option value="">Select an option...</option>
          <option value="Google">Google</option>
          <option value="Social Media">Social Media</option>
          <option value="Referral">Referral</option>
          <option value="Other">Other</option>
        </select>
      </div>

      <div className={styles.checkboxGroup}>
        <label className={styles.checkboxLabel}>
          <input type="checkbox" name="consent" required defaultChecked={values.consent} />
          I consent to my data being used to contact me regarding my request.
        </label>
      </div>

      <ValidationError prefix="Email" field="email" errors={state.errors} />

      <div className={styles.buttonGroup}>
        <button onClick={prevStep} type="button" className={styles.prevButton}>Back</button>
        <button type="submit" disabled={state.submitting} className={styles.submitButton}>
          {state.submitting ? "Submitting..." : "Submit Request"}
        </button>
      </div>
    </form>
  );
};

export default ContactPage;
